import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/Store';

interface VendorCountInfo {
  orderReviewCounts: OrderReviewCounts;
  unansweredQuestionCounts: OrderReviewCounts;
  shipmentCounts: ShipmentCounts;
}

interface ShipmentCounts {
  totalItemCountOneMonth: number;
  totalItemCountThreeMonth: number;
  totalItemCountOneMonthInCargo: number;
  totalItemCountThreeMonthInCargo: number;
}

interface OrderReviewCounts {
  totalItemCountOneMonth: number;
  totalItemCountThreeMonth: number;
}

interface VendorScoreInfo {
  vendorScoreList: VendorScoreList[];
  vendorScore: string;
}

interface VendorScoreList {
  id: number;
  title: string;
  description: null;
  target: number;
  last30DaysPerformance: number;
  last30to90DaysPerformance: number;
  vendorScore: number;
}

export interface IVendorReconciliation {
  id: number;
  periodId: number;
  periodName: string;
  lastBalance: string;
  message: string;
  status: string;
  statusMessage: string;
  files: any[];
  rejectMessage: null;
  rejectedBy: null;
  rejectedAt: null;
  approvedBy: null;
  approvedAt: null;
  createdAt: string;
}

export type DSalesPerformaceParams = 'daily' | 'sevenDays' | 'thirtyDays';

const VENDOR_MC_PIM_URL = `${process.env.REACT_APP_API_PIM_URL}`;
const VENDOR_MC_ESCROW_URL = `${process.env.REACT_APP_API_ESCROW_URL}`;
const VENDOR_MC_OMS_URL = `${process.env.REACT_APP_API_OMS_URL}`;

const getDashbordQueryKey: any = 'dashboard-list';

export const useChartList = (params?: { operationFilter: DSalesPerformaceParams }, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();

  const fetchInventory = async () => {
    const res = await axios.get(`${VENDOR_MC_OMS_URL}/dashboard/${user?.vendor?.id}/v2`, { params });
    queryClient.setQueryData([getDashbordQueryKey], (oldData: any) => {
      let newItem: any = {};
      if (params && params.operationFilter === 'daily') newItem = { dailyChart: res.data?.dailyChart };
      else if (params && params.operationFilter === 'sevenDays') newItem = { sevenDayChart: res.data?.sevenDayChart };
      else if (params && params.operationFilter === 'thirtyDays') newItem = { thirtyDayChart: res.data?.thirtyDayChart };

      return {
        ...oldData,
        oms: {
          ...oldData?.oms,
          ...newItem,
        },
      };
    });
    return res?.data || {};
  };

  return useQuery(['chart-list'], fetchInventory, {
    refetchOnWindowFocus: false,
    onError,
    enabled: false,
  });
};

export const useDashboardList = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);

  const fetchInventory = async () => {
    //PIM
    const vendorCountInfoPromise = axios.get<any>(`${VENDOR_MC_PIM_URL}/vendor/${user?.vendor.id}/count-info`);
    const vendorInfoPromise = axios.get<any>(`${VENDOR_MC_PIM_URL}/vendor/${user?.vendor?.id}/show`);

    //ESCROW
    const escrowPromise = axios.get<any>(`${VENDOR_MC_ESCROW_URL}/${user?.vendor?.id}/dashboard`);

    //OMS
    const omsPromise = axios.get<any>(`${VENDOR_MC_OMS_URL}/dashboard/${user?.vendor?.id}/v2`);

    return await Promise.all([vendorCountInfoPromise, vendorInfoPromise, escrowPromise, omsPromise])
      .then((results) => {
        const [vendorCountInfoResponse, vendorInfoResponse, escrowResponse, omsResponse] = results;
        return {
          vendorCountInfo: vendorCountInfoResponse.data,
          vendorInfo: vendorInfoResponse.data,
          escrow: escrowResponse.data,
          oms: omsResponse.data,
        };
      })
      .catch((error) => {
        // Hata yönetimi
        console.error('Bir hata oluştu:', error);
        throw error;
      });
  };

  return useQuery([getDashbordQueryKey], fetchInventory, {
    refetchOnWindowFocus: false,
    onError,
  });
};

export const useSellerEvaluation = (onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);

  const fetchInventory = async () => {
    const countInfo = (await axios.get<VendorCountInfo>(`${VENDOR_MC_PIM_URL}/vendor-score/${user?.vendor.id}/count-info`)).data;
    const scoreInfo = (await axios.get<VendorScoreInfo>(`${VENDOR_MC_PIM_URL}/vendor-score/${user?.vendor.id}/score-info`)).data;
    return { scoreInfo, countInfo };
  };

  return useQuery(['seller-evaluation'], fetchInventory, {
    refetchOnWindowFocus: false,
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || err.response?.data?.message || err?.message || 'Bir sorun oluştu.', { autoClose: 2000 });
    },
  });
};

export const useVendorReconciliation = () => {
  const { user } = useAppSelector((state) => state.auth);

  const fetchInventory = async () => {
    const response = await axios.get<IVendorReconciliation>(`${VENDOR_MC_ESCROW_URL}/${user?.vendor.id}/reconciliation/waiting`);
    return response.data;
  };

  return useQuery(['vendor-reconciliation'], fetchInventory, {
    refetchOnWindowFocus: false,
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || err.response?.data?.message || err?.message || 'Bir sorun oluştu.', { autoClose: 2000 });
    },
  });
};

export const useVendorReconciliationFileUpload = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  return useMutation({
    mutationKey: ['vendor-reconciliation-file-upload'],
    mutationFn: async (formPayload: any) => {
      var formData = new FormData();
      for await (const file of formPayload?.files) {
        formData.append('file', file);
      }
      const response = await axios.post(`${VENDOR_MC_ESCROW_URL}/${user?.vendor.id}/reconciliation/file-upload/${formPayload?.vendorReconciliation}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { ...response.data, fileName: formPayload?.files?.[0]?.name };
    },
    onSuccess,
    onError,
  });
};

export const useVendorReconciliationFileRemove = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  return useMutation({
    mutationKey: ['vendor-reconciliation-file-remove'],
    mutationFn: async (formPayload: any) => {
      await axios.post(`${VENDOR_MC_ESCROW_URL}/${user?.vendor.id}/reconciliation/file-remove-url/${formPayload?.vendorReconciliation}`, formPayload);
      return { url: formPayload?.fileUrl };
    },
    onSuccess,
    onError,
  });
};

export const useVendorReconciliationReject = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  return useMutation({
    mutationKey: ['vendor-reconciliation-reject'],
    mutationFn: async (formPayload: any) => {
      const response = await axios.post(`${VENDOR_MC_ESCROW_URL}/${user?.vendor.id}/reconciliation/reject/${formPayload?.vendorReconciliation}`, formPayload);
      await axios.post(`${VENDOR_MC_PIM_URL}/${user?.vendor.id}/ticket-consensus-create`, {
        comment: formPayload?.rejectMessage,
        attachments: formPayload?.fileUrls,
      });
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useVendorReconciliationApprove = (onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  return useMutation({
    mutationKey: ['vendor-reconciliation-approve'],
    mutationFn: async (formPayload: any) => {
      const response = await axios.post(`${VENDOR_MC_ESCROW_URL}/${user?.vendor.id}/reconciliation/approve/${formPayload?.vendorReconciliation}`, formPayload);
      return response?.data;
    },
    onSuccess,
    onError,
  });
};
